import React from 'react';

/**
 * Safely escape HTML to prevent XSS
 */
export function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Decode HTML entities in a string
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

// Define whitelist of allowed URL protocols
const ALLOWED_PROTOCOLS = ['http:', 'https:'];

// Blocked URL domains to prevent malicious links (e.g., known phishing sites)
// Most legitimate domains are allowed by default
const BLOCKED_DOMAINS = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '[::1]'
];

/**
 * Validate and sanitize URLs
 */
export function sanitizeUrl(url: string): string | null {
  try {
    // First decode any HTML entities in the URL
    const decodedUrl = decodeHtmlEntities(url);

    const urlObj = new URL(decodedUrl);

    // Check protocol - only allow http and https
    if (!ALLOWED_PROTOCOLS.includes(urlObj.protocol)) {
      console.warn('Blocked URL with disallowed protocol:', urlObj.protocol);
      return null;
    }

    // Check if domain is blocked
    const hostname = urlObj.hostname.toLowerCase();
    if (BLOCKED_DOMAINS.some(blocked => hostname === blocked || hostname.includes(blocked))) {
      console.warn('Blocked URL with disallowed domain:', hostname);
      return null;
    }

    // Remove any username/password for security
    urlObj.username = '';
    urlObj.password = '';

    return urlObj.toString();
  } catch (error) {
    console.warn('Invalid URL:', error);
    return null;
  }
}

/**
 * Extract YouTube video ID from a URL
 */
function extractYouTubeId(url: string): string | null {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname === 'youtu.be') {
      return urlObj.pathname.slice(1);
    }
    if (urlObj.hostname === 'youtube.com' || urlObj.hostname === 'www.youtube.com') {
      return urlObj.searchParams.get('v');
    }
  } catch (error) {
    console.warn('Invalid YouTube URL:', error);
  }
  return null;
}

/**
 * Clean up duplicated URLs in text
 */
export function cleanupDuplicatedUrls(text: string): string {
  if (!text) return '';
  
  // Remove duplicated URLs
  let cleaned = text;
  
  // First, clean up any duplicated URLs that are exactly the same
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urls = text.match(urlRegex) || [];
  
  // Create a Set to track unique URLs
  const uniqueUrls = new Set<string>();
  
  // Replace duplicated URLs with a single instance
  urls.forEach(url => {
    if (!uniqueUrls.has(url)) {
      uniqueUrls.add(url);
    } else {
      // If we've seen this URL before, remove additional occurrences
      const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const duplicateRegex = new RegExp(`(${escapedUrl})\\s*\\1+`, 'g');
      cleaned = cleaned.replace(duplicateRegex, '$1');
    }
  });
  
  // Clean up any HTML-encoded URLs that are duplicated
  cleaned = cleaned.replace(/(&lt;a\s+href=(?:&quot;|")(.+?)(?:&quot;|")&gt;.+?&lt;\/a&gt;)\s*\1/g, '$1');
  
  // Clean up duplicated URL patterns like URL/URL/URL
  cleaned = cleaned.replace(/(https?:\/\/[^\s]+)\/(https?:\/\/[^\s]+)\/(https?:\/\/[^\s]+)/g, '$1');
  
  return cleaned;
}

type SafeContent = string | JSX.Element;

/**
 * Create safe HTML from text with URLs
 */
export function createSafeHtml(text: string): SafeContent[] {
  if (!text) return [];

  // First, decode any HTML entities (like &lt;a href=&quot;...&gt;)
  const decodedText = decodeHtmlEntities(text);

  // Remove any HTML anchor tags to extract just the URLs
  // This handles cases like <a href="https://example.com">https://example.com</a>
  const cleanedText = decodedText.replace(/<a\s+href=["']([^"']+)["'][^>]*>([^<]*)<\/a>/gi, '$1');

  // Match URLs in the text
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;

  // First split the text by URLs
  const parts = cleanedText.split(urlRegex);
  const result: SafeContent[] = [];

  // Process each part
  parts.forEach((part, index) => {
    // Check if this part is a URL
    if (part.match(urlRegex)) {
      // It's a URL, create a link
      const safeUrl = sanitizeUrl(part);
      if (safeUrl) {
        result.push(
          React.createElement('a', {
            key: `link-${index}`,
            href: safeUrl,
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
            className: 'text-blue-300 hover:text-blue-200 underline',
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            children: part
          })
        );
      } else {
        // If URL is not safe, just add as text
        result.push(escapeHtml(part));
      }
    } else {
      // It's regular text, handle line breaks and escape HTML
      const escapedPart = escapeHtml(part);
      const lines = escapedPart.split('\n');

      lines.forEach((line, lineIndex) => {
        if (lineIndex > 0) {
          // Add a line break between lines
          result.push(React.createElement('br', { key: `br-${index}-${lineIndex}` }));
        }
        if (line) result.push(line);
      });
    }
  });

  return result;
}